import { UpdateThresholdBody } from 'src/pages/admin/services/xbitRelayService';

import {
  ALERT_CONFIG_LOCKED_CHAIN_IDS,
  REBALANCE_CONFIG_LOCKED_CHAIN_IDS,
} from './constants';

/** One flattened wallet×token row as produced by this module's data provider. */
export type HotWalletRow = {
  provider: string;
  chainId: number;
  tokenAddress?: string;
  isNative?: boolean;
  alertThreshold?: string;
  alertEnabled?: boolean;
  rebalanceCeiling?: string;
  rebalanceTarget?: string;
  rebalanceEnabled?: boolean;
};

/** The four editable fields of a threshold-config row. */
export type ThresholdConfigEdit = {
  threshold?: string;
  enabled?: boolean;
  ceiling?: string;
  target?: string;
  rebalanceEnabled?: boolean;
};

/**
 * The BE accepts `^(?:0|[1-9]\d*)(?:\.\d+)?$` only — no sign, no leading zeros, no scientific
 * notation, not empty. Anything else is a 400, so it is rejected in the UI first.
 */
const CONFIG_DECIMAL = /^(?:0|[1-9]\d*)(?:\.\d+)?$/;

export const isValidConfigDecimal = (value?: string | null): boolean =>
  CONFIG_DECIMAL.test((value ?? '').trim());

/**
 * Normalizes a value the BE would reject into one it accepts, without changing its magnitude:
 * `''` → `'0'`, `'01'` → `'1'`, `'.5'` → `'0.5'`, `'1.'` → `'1'`. Returns the input untouched
 * when it cannot be normalized, so the caller's validation still catches it.
 */
export const normalizeConfigDecimal = (value?: string | null): string => {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return '0';
  if (isValidConfigDecimal(trimmed)) return trimmed;
  if (!/^\d*\.?\d*$/.test(trimmed)) return trimmed;

  const [rawWhole = '', rawFraction = ''] = trimmed.split('.');
  const whole = rawWhole.replace(/^0+(?=\d)/, '') || '0';
  const fraction = rawFraction.replace(/0+$/, '');
  const normalized = fraction ? `${whole}.${fraction}` : whole;
  return isValidConfigDecimal(normalized) ? normalized : trimmed;
};

/**
 * ⚠️ The reason this exists.
 *
 * `PUT /hot-wallets/:provider/threshold-config` is a FULL REPLACE: wallet-service overwrites the
 * whole config row every call. Sending only the field the operator edited silently wipes the
 * others — editing an alert threshold would clear the rebalance ceiling/target and switch
 * auto-rebalance off. So every save rebuilds all eight fields from the current row and applies
 * just the edited ones on top.
 */
export const buildThresholdConfigBody = (
  row: HotWalletRow,
  edit: ThresholdConfigEdit = {}
): UpdateThresholdBody => ({
  chain_id: row.chainId,
  token_address: row.tokenAddress ?? '',
  is_native: Boolean(row.isNative),
  threshold: normalizeConfigDecimal(edit.threshold ?? row.alertThreshold),
  enabled: edit.enabled ?? row.alertEnabled ?? false,
  ceiling: normalizeConfigDecimal(edit.ceiling ?? row.rebalanceCeiling),
  target: normalizeConfigDecimal(edit.target ?? row.rebalanceTarget),
  rebalance_enabled: edit.rebalanceEnabled ?? row.rebalanceEnabled ?? false,
});

/**
 * Compares two non-negative decimal strings digit-wise. `Number()` would lose precision on the
 * large token amounts these fields hold, so the comparison never leaves string space.
 */
export const compareDecimals = (left: string, right: string): number => {
  const [leftWhole = '0', leftFraction = ''] = left.split('.');
  const [rightWhole = '0', rightFraction = ''] = right.split('.');

  const wholeWidth = Math.max(leftWhole.length, rightWhole.length);
  const paddedLeftWhole = leftWhole.padStart(wholeWidth, '0');
  const paddedRightWhole = rightWhole.padStart(wholeWidth, '0');
  if (paddedLeftWhole !== paddedRightWhole) {
    return paddedLeftWhole < paddedRightWhole ? -1 : 1;
  }

  const fractionWidth = Math.max(leftFraction.length, rightFraction.length);
  const paddedLeftFraction = leftFraction.padEnd(fractionWidth, '0');
  const paddedRightFraction = rightFraction.padEnd(fractionWidth, '0');
  if (paddedLeftFraction === paddedRightFraction) return 0;
  return paddedLeftFraction < paddedRightFraction ? -1 : 1;
};

/**
 * Valid configuration is `threshold < target < ceiling`.
 *
 * `ceiling` is the level that triggers a rebalance and `target` is where the balance lands
 * afterwards, so a target at or above the ceiling would re-trigger immediately. Below the alert
 * threshold it would land straight into a low-balance alert. Both bounds are strict — landing
 * exactly on either one is the degenerate case, not a valid setting.
 *
 * False whenever any of the three is not a valid decimal, so callers get one answer to act on.
 */
export const isRebalanceRangeValid = (
  threshold?: string | null,
  target?: string | null,
  ceiling?: string | null
): boolean => {
  if (![threshold, target, ceiling].every(isValidConfigDecimal)) return false;

  const low = String(threshold).trim();
  const mid = String(target).trim();
  const high = String(ceiling).trim();

  return compareDecimals(mid, low) > 0 && compareDecimals(high, mid) > 0;
};

/** `0` is a real chain id here, so parse rather than testing truthiness. */
const toChainId = (chainId?: number | string | null): number | undefined => {
  if (chainId === undefined || chainId === null || chainId === '') return undefined;
  const parsed = Number(chainId);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Some chains do not support threshold config at all. Their controls render read-only rather than
 * disappearing, so the stored values stay visible.
 */
export const isAlertConfigLocked = (chainId?: number | string | null): boolean => {
  const id = toChainId(chainId);
  return id !== undefined && (ALERT_CONFIG_LOCKED_CHAIN_IDS as readonly number[]).includes(id);
};

/** Locked wherever alerts are locked, plus the chains that support alerts but not rebalancing. */
export const isRebalanceConfigLocked = (chainId?: number | string | null): boolean => {
  const id = toChainId(chainId);
  return id !== undefined && (REBALANCE_CONFIG_LOCKED_CHAIN_IDS as readonly number[]).includes(id);
};

/** Why a row's rebalance controls are read-only, or `null` when they are editable. */
export type RebalanceLockReason = 'chain' | 'native';

/**
 * Rebalancing moves a token between hot wallets, which a chain's native asset cannot do — it is
 * the gas it would be moved with. Alert settings stay editable either way, so this is deliberately
 * separate from `isAlertConfigLocked`.
 *
 * The chain reason wins when both apply: no amount of token switching unlocks an unsupported chain.
 */
export const rebalanceLockReason = (
  row: Pick<HotWalletRow, 'chainId' | 'isNative'>
): RebalanceLockReason | null => {
  if (row.isNative) return 'native';
  if (isRebalanceConfigLocked(row.chainId)) return 'chain';
  return null;
};
